import { promises as fs } from 'node:fs';
import path from 'node:path';
import { proxyDynamicDir } from './index.js';
import {
  serviceProxyAlias,
  type HttpService,
} from '../config/http-services.js';

/**
 * Traefik file-provider dynamic configuration: one yml per container
 * under `<MONOCEROS_HOME>/traefik/dynamic/<name>.yml`. Traefik picks
 * up file changes within ~100 ms via `--providers.file.watch=true`,
 * so add-port / remove-port effects land hot without a restart of
 * either Traefik or the dev-container. See ADR 0007.
 *
 * Hostname layout (`*.localhost` resolves to 127.0.0.1 by RFC 6761):
 *
 *   - `<name>.localhost`              → the *first* declared port (default)
 *   - `<name>-<port>.localhost`       → that specific internal port
 *
 * Backend URL is always `http://<name>:<port>` — the container joins
 * the `monoceros-proxy` network under the yml name (set via
 * `--network-alias` for image-mode and a `networks.monoceros-proxy.aliases:`
 * entry for compose-mode in `create/scaffold.ts`).
 */

export interface DynamicConfigOptions {
  /** Override the resolved MONOCEROS_HOME (tests inject a tmpdir). */
  monocerosHome?: string;
}

/** Path the dynamic config for one container lives at. */
export function dynamicConfigPath(
  name: string,
  opts: DynamicConfigOptions = {},
): string {
  return path.join(proxyDynamicDir(opts.monocerosHome), `${name}.yml`);
}

/**
 * Write the per-container dynamic config. The directory under
 * `<MONOCEROS_HOME>/traefik/dynamic/` is created if needed (matches
 * what ensureProxy does — calling either before the other is fine).
 *
 * Idempotent: a second call with the same `(name, ports)` produces a
 * byte-identical file. Returns the absolute path that was written.
 *
 * `ports` must be non-empty; the caller (add-port, apply) is
 * responsible for routing empty-list cases through
 * `removeDynamicConfig`.
 */
export async function writeDynamicConfig(
  name: string,
  ports: number[],
  opts: DynamicConfigOptions & { services?: readonly HttpService[] } = {},
): Promise<string> {
  const services = opts.services ?? [];
  if (ports.length === 0 && services.length === 0) {
    throw new Error(
      `writeDynamicConfig requires at least one port or exposed service. For neither, call removeDynamicConfig(${JSON.stringify(name)}).`,
    );
  }
  const dir = proxyDynamicDir(opts.monocerosHome);
  await fs.mkdir(dir, { recursive: true });
  const file = path.join(dir, `${name}.yml`);
  await fs.writeFile(file, renderDynamicConfig(name, ports, services), 'utf8');
  return file;
}

/** Drop the dynamic config for `name`. No-op when the file is absent. */
export async function removeDynamicConfig(
  name: string,
  opts: DynamicConfigOptions = {},
): Promise<void> {
  const file = path.join(proxyDynamicDir(opts.monocerosHome), `${name}.yml`);
  await fs.rm(file, { force: true });
}

/**
 * Render the yml body. Pure function — no I/O — so unit tests can
 * snapshot the output cheaply, and `writeDynamicConfig` is the only
 * one that touches the filesystem.
 *
 * First port doubles as the container's default URL via
 * `<name>.localhost`; every port also gets its explicit
 * `<name>-<port>.localhost` route. Both routers point at the same
 * load-balancer service backend.
 */
export function renderDynamicConfig(
  name: string,
  ports: number[],
  services: readonly HttpService[] = [],
): string {
  const lines: string[] = [];
  lines.push('# Generated by Monoceros — do not edit by hand.');
  lines.push(`# Container: ${name}`);
  lines.push(`# Ports: ${ports.join(', ') || '(none)'}`);
  if (services.length > 0) {
    lines.push(
      `# Services: ${services.map((s) => `${s.name}:${s.port}`).join(', ')}`,
    );
  }
  lines.push('# Traefik file-provider re-reads this on change (~100 ms);');
  lines.push(
    '# to change routing, edit container-configs/' + name + '.yml or use',
  );
  lines.push('# `monoceros add-port` / `monoceros remove-port`.');
  lines.push('http:');
  lines.push('  routers:');
  ports.forEach((port, idx) => {
    const router = `${name}-${port}`;
    const hostExplicit = `${name}-${port}.localhost`;
    // The first declared port doubles as the container default; its
    // router matches both the explicit `<name>-<port>.localhost` and
    // the bare `<name>.localhost`. The others match only their
    // explicit hostname.
    const rule =
      idx === 0
        ? `"Host(\`${name}.localhost\`) || Host(\`${hostExplicit}\`)"`
        : `"Host(\`${hostExplicit}\`)"`;
    lines.push(`    ${router}:`);
    lines.push(`      rule: ${rule}`);
    lines.push(`      service: ${router}`);
    lines.push('      entryPoints:');
    lines.push('        - web');
  });
  // A service routes by its own name, never as the container default: the bare
  // `<name>.localhost` stays with the first declared port, so adding a service
  // never moves an address the builder already uses.
  for (const svc of services) {
    const alias = serviceProxyAlias(name, svc.name);
    lines.push(`    ${alias}:`);
    lines.push(`      rule: "Host(\`${alias}.localhost\`)"`);
    lines.push(`      service: ${alias}`);
    lines.push('      entryPoints:');
    lines.push('        - web');
  }
  lines.push('  services:');
  for (const port of ports) {
    const svc = `${name}-${port}`;
    lines.push(`    ${svc}:`);
    lines.push('      loadBalancer:');
    lines.push('        servers:');
    lines.push(`          - url: "http://${name}:${port}"`);
  }
  // The backend is the service's alias on the proxy network, not its bare
  // compose name: that network is machine-wide, so only the prefixed alias is
  // unique across workbenches (see create/scaffold.ts).
  for (const svc of services) {
    const alias = serviceProxyAlias(name, svc.name);
    lines.push(`    ${alias}:`);
    lines.push('      loadBalancer:');
    lines.push('        servers:');
    lines.push(`          - url: "http://${alias}:${svc.port}"`);
  }
  return lines.join('\n') + '\n';
}

/**
 * URLs the proxy exposes for a container. Pure derivation — used by
 * the `monoceros port` command and by `add-port` to print the
 * resulting routes after a write. `hostPort` (default 80) gets
 * suffixed to the URL only when it's non-default — the URLs stay
 * clean for the common case.
 */
export interface ProxyUrl {
  /** Internal container port. */
  port: number;
  /** `http://<name>-<port>.localhost[:<hostPort>]`. */
  url: string;
  /** True for the first port: also reachable at `<name>.localhost`. */
  isDefault: boolean;
}

export function proxyUrlsFor(
  name: string,
  ports: number[],
  hostPort: number = 80,
): ProxyUrl[] {
  const portSuffix = hostPort === 80 ? '' : `:${hostPort}`;
  return ports.map((port, idx) => ({
    port,
    url: `http://${name}-${port}.localhost${portSuffix}`,
    isDefault: idx === 0,
  }));
}

/**
 * The URL an exposed service answers on through the proxy. Same shape as
 * `proxyUrlsFor` for ports: the host port is only spelled out when it is not
 * the default 80, so the common case stays clean.
 */
export function serviceProxyUrl(
  containerName: string,
  serviceName: string,
  hostPort: number = 80,
): string {
  const suffix = hostPort === 80 ? '' : `:${hostPort}`;
  return `http://${serviceProxyAlias(containerName, serviceName)}.localhost${suffix}`;
}
